import { Timestamp } from 'firebase/firestore';

export interface ApplyCouponRequest {
  code: string;
}

export interface ApplyCouponResponse {
  isValid: boolean;
  message: string;
  coupon?: Coupon;
}

export interface ChangePlanRequest {
  planId: string;
  couponCode?: string;
}

export interface ChangePlanResponse {
  success: boolean;
  message: string;
  redirectUrl?: string;
}

/**
 * Available subscription plans in the system
 */
export enum PlanType {
  FREE = 'FREE',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  TIME_LIMITED = 'TIME_LIMITED', // New plan type for time-limited access
}

/**
 * Human readable labels for plan types
 */
export const PLAN_TYPE_LABELS: Record<PlanType, string> = {
  [PlanType.FREE]: 'Free',
  [PlanType.MONTHLY]: 'Monthly Subscription',
  [PlanType.YEARLY]: 'Yearly Subscription',
  [PlanType.TIME_LIMITED]: 'Time-Limited Access',
};

/**
 * Plan limits configuration
 */
export interface PlanLimits {
  maxTests: number;
  maxPosts: number;
  maxConnections: number;
  isUnlimited: boolean;
}

/**
 * Plan pricing details
 */
export interface PlanPricing {
  amount: number;
  currency: string;
  interval?: 'month' | 'year' | null;
  stripePriceId?: string;
}

/**
 * Subscription plan interface
 */
export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  type: PlanType;
  isActive: boolean;
  isPopular: boolean;
  limits: PlanLimits;
  pricing: PlanPricing;
  features: string[];
  metadata?: Record<string, any>;
}

/**
 * Subscription status types
 */
export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
  PAST_DUE = 'PAST_DUE',
  UNPAID = 'UNPAID',
  TRIAL = 'TRIAL',
  EXPIRED = 'EXPIRED',
}

/**
 * User subscription interface
 */
export interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  status: SubscriptionStatus;
  currentPeriodStart: Timestamp;
  currentPeriodEnd: Timestamp;
  cancelAtPeriodEnd: boolean;
  canceledAt?: Timestamp;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  autoRenew?: boolean;
  planDetails?: SubscriptionPlan;
  metadata?: Record<string, any>;
}

/**
 * Coupon discount type
 */
export enum CouponDiscountType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  TIME_LIMITED_ACCESS = 'TIME_LIMITED_ACCESS'
}

/**
 * Coupon interface
 */
export interface Coupon {
  id: string;
  code: string;
  description: string;
  discountValue: number;
  discountType: CouponDiscountType;
  expiresAt: Timestamp;
  applicablePlanIds?: string[]; // Array of plan IDs this coupon can be applied to
  accessDurationMonths?: number; // Duration of access in months (1 or 3) for TIME_LIMITED_ACCESS coupons
  requiresPayment?: boolean; // Indicates if the coupon requires payment or not
}

/**
 * Coupon usage record
 */
export interface CouponUsage {
  id: string;
  couponId: string;
  userId: string;
  subscriptionId: string;
  usedAt: Timestamp;
}

/**
 * Subscription transaction record
 */
export interface SubscriptionTransaction {
  id: string;
  userId: string;
  subscriptionId: string;
  planId: string;
  amount: number;
  currency: string;
  status: 'succeeded' | 'failed' | 'pending';
  couponId?: string;
  discountAmount?: number;
  stripePaymentIntentId?: string;
  metadata?: Record<string, any>;
}

/**
 * Represents a single item in the subscription history
 */
export interface SubscriptionHistoryItem {
  id: string;
  userId: string;
  subscriptionId: string;
  timestamp: Timestamp;
  changeType: string;
  description: string;
  details?: Record<string, any>;
}
